/**
 * RichTextEditor
 * ──────────────
 * WYSIWYG editor for product description fields (stored as HTML, rendered with
 * dangerouslySetInnerHTML on the storefront). Gives three views so the user never
 * has to hand-write HTML:
 *   - "تصميم"  visual editor (Tiptap) — bold/italic/underline/lists/links via toolbar
 *   - "معاينة" read-only preview styled exactly like the storefront product page
 *   - "الكود"  raw HTML source, for anyone who does want to edit markup directly
 *
 * Only tags the storefront PDP actually styles (p, ul, ol) plus safe inline
 * emphasis/links are supported — the server re-sanitizes on save regardless
 * (see hagatna-backend `@shared/validation/description`), so this is about a
 * good editing experience, not the security boundary.
 *
 * Props:
 *   value       — current HTML string
 *   onChange    — (html) => void
 *   placeholder — shown in the visual editor when empty
 *   dir         — 'rtl' | 'ltr', matches the field's language
 */
import { useEffect, useRef, useState } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
  Link2, Unlink, Undo2, Redo2, Eye, Code2, Pencil,
} from 'lucide-react'

const TABS = [
  { id: 'visual', label: 'تصميم', icon: Pencil },
  { id: 'preview', label: 'معاينة', icon: Eye },
  { id: 'code', label: 'الكود', icon: Code2 },
]

function normalizeUrl(raw) {
  const v = raw.trim()
  if (!v) return null
  if (/^(https?:|mailto:)/i.test(v)) return v
  if (/^[^/]+@[^/]+\.[^/]+$/.test(v)) return `mailto:${v}`
  return `https://${v}`
}

function ToolbarButton({ active, onClick, title, disabled, children }) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => e.preventDefault()} // keep editor selection when clicking the toolbar
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 28, height: 28, borderRadius: 8, border: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: active ? '#000666' : 'transparent',
        color: active ? '#fff' : disabled ? '#C4C4C4' : '#454652',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 0.15s, color 0.15s',
      }}
    >
      {children}
    </button>
  )
}

function Toolbar({ editor }) {
  const [linkOpen, setLinkOpen] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const inputRef = useRef(null)

  useEffect(() => {
    if (linkOpen) inputRef.current?.focus()
  }, [linkOpen])

  if (!editor) return null

  const openLinkPopover = () => {
    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run()
      return
    }
    setLinkUrl('')
    setLinkOpen(true)
  }

  const confirmLink = () => {
    const href = normalizeUrl(linkUrl)
    if (href) editor.chain().focus().extendMarkRange('link').setLink({ href }).run()
    setLinkOpen(false)
  }

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '4px 6px', borderBottom: '1px solid #EBEBEB', flexWrap: 'wrap' }}>
        <ToolbarButton title="غامق" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
          <Bold size={14} />
        </ToolbarButton>
        <ToolbarButton title="مائل" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <Italic size={14} />
        </ToolbarButton>
        <ToolbarButton title="تسطير" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}>
          <UnderlineIcon size={14} />
        </ToolbarButton>
        <div style={{ width: 1, height: 18, background: '#EBEBEB', margin: '0 4px' }} />
        <ToolbarButton title="قائمة نقطية" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <List size={14} />
        </ToolbarButton>
        <ToolbarButton title="قائمة مرقمة" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <ListOrdered size={14} />
        </ToolbarButton>
        <div style={{ width: 1, height: 18, background: '#EBEBEB', margin: '0 4px' }} />
        <ToolbarButton title={editor.isActive('link') ? 'إزالة الرابط' : 'إضافة رابط'} active={editor.isActive('link')} onClick={openLinkPopover}>
          {editor.isActive('link') ? <Unlink size={14} /> : <Link2 size={14} />}
        </ToolbarButton>
        <div style={{ width: 1, height: 18, background: '#EBEBEB', margin: '0 4px' }} />
        <ToolbarButton title="تراجع" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}>
          <Undo2 size={14} />
        </ToolbarButton>
        <ToolbarButton title="إعادة" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}>
          <Redo2 size={14} />
        </ToolbarButton>
      </div>

      {linkOpen && (
        <div style={{
          position: 'absolute', top: '100%', insetInlineStart: 6, zIndex: 5, marginTop: 4,
          display: 'flex', gap: 6, background: '#fff', border: '1px solid #EBEBEB',
          borderRadius: 10, padding: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        }}>
          <input
            ref={inputRef}
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); confirmLink() }
              if (e.key === 'Escape') { e.preventDefault(); setLinkOpen(false) }
            }}
            placeholder="example.com"
            dir="ltr"
            style={{ width: 180, padding: '6px 10px', borderRadius: 8, border: '1.5px solid #EBEBEB', fontSize: 12, outline: 'none' }}
          />
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={confirmLink}
            style={{ padding: '0 12px', borderRadius: 8, border: 'none', background: '#000666', color: '#fff', fontSize: 12, fontWeight: 700, fontFamily: 'Cairo, sans-serif', cursor: 'pointer' }}>
            إضافة
          </button>
        </div>
      )}
    </div>
  )
}

export default function RichTextEditor({ value, onChange, placeholder, dir = 'rtl' }) {
  const [mode, setMode] = useState('visual')

  const editor = useEditor({
    extensions: [
      // Tiptap v3's StarterKit already bundles link/underline — disabled here since
      // we register our own below with custom config (openOnClick, allowed protocols).
      StarterKit.configure({ heading: false, blockquote: false, horizontalRule: false, codeBlock: false, code: false, strike: false, link: false, underline: false }),
      Underline,
      Link.configure({ openOnClick: false, autolink: false, protocols: ['http', 'https', 'mailto'] }),
      Placeholder.configure({ placeholder: placeholder || '' }),
    ],
    content: value || '',
    editorProps: { attributes: { class: 'hg-richtext-content', dir } },
    onUpdate: ({ editor }) => onChange(editor.getHTML() === '<p></p>' ? '' : editor.getHTML()),
  })

  // Keep the editor in sync when `value` changes from outside (e.g. initial data
  // arriving async in edit mode) without fighting the user's own typing/cursor.
  useEffect(() => {
    if (!editor) return
    if (editor.isFocused) return
    if ((value || '') === editor.getHTML()) return
    editor.commands.setContent(value || '', false)
  }, [value, editor])

  return (
    <div style={{ border: '1.5px solid #EBEBEB', borderRadius: 12, background: '#FAFAFA', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4, padding: '6px 6px 0' }}>
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setMode(id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: '8px 8px 0 0',
              border: 'none', borderBottom: mode === id ? '2px solid #000666' : '2px solid transparent',
              background: 'transparent', fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 11,
              color: mode === id ? '#000666' : '#767683', cursor: 'pointer',
            }}
          >
            <Icon size={12} />
            {label}
          </button>
        ))}
      </div>

      {mode === 'visual' && (
        <div style={{ background: '#fff' }}>
          <Toolbar editor={editor} />
          <EditorContent editor={editor} style={{ minHeight: 110 }} />
        </div>
      )}

      {mode === 'preview' && (
        value ? (
          <div className="hg-richtext-content" dir={dir} dangerouslySetInnerHTML={{ __html: value }} style={{ minHeight: 110, background: '#fff' }} />
        ) : (
          <div style={{ minHeight: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#C4C4C4', fontSize: 12, fontFamily: 'Tajawal, sans-serif', background: '#fff' }}>
            لا يوجد وصف بعد
          </div>
        )
      )}

      {mode === 'code' && (
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          dir="ltr"
          rows={6}
          style={{
            width: '100%', boxSizing: 'border-box', minHeight: 110, padding: '10px 14px',
            border: 'none', outline: 'none', resize: 'vertical', background: '#fff',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 12, color: '#1A1C1C',
          }}
        />
      )}
    </div>
  )
}
